import React, { useState, useEffect, useContext, ReactElement } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  Button,
  Paper,
  Grid,
  makeStyles,
  Typography,
  TableCell,
  Box,
  Divider,
  Link,
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import useInput from "../../hooks/input/useInput";
import useTable from "../../hooks/table/useTable";
import LinkAdapter from "../../components/shared/common/LinkAdapter";
import TrTable from "../../components/shared/controls/TrTable";
import TrTableHeadMetadata from "../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../models/shared/TrTableBodyData";
import PagedResult from "../../models/shared/PagedResult";
import PaymentRequestList from "../../models/payment-request/PaymentRequestList";
import PaymentRequestStatusEnum, {
  PaymentRequestStatus,
} from "../../models/payment-request-status/PaymentRequestStatusEnum";
import PaymentRequestApiClient from "../../api/PaymentRequestApiClient";
import {
  paymentRequestDetailsUrl,
  paymentRequestNewUrl,
  paymentRequestEditUrl,
} from "../../urls";
import { currencyFormat, nameof } from "../../utilities/utilities";
import TrSelect from "../shared/controls/TrSelect";
import TrSelectData from "../../models/shared/TrSelectData";
import YearFilter from "../../models/shared/YearFilter";
import MonthFilter from "../../models/shared/MonthFilter";
import PaymentRequestFilter from "../../models/payment-request/PaymentRequestFilter";
import PaymentRequestFilterValue from "../../models/payment-request/PaymentRequestFilterValue";
import PaymentRequestStatusElement from "../shared/controls/PaymentRequestStatusElement";

export default function PaymentRequestListPage({
  history,
}: RouteComponentProps) {
  const classes = useStyles({});
  const [tabIndex, setTabIndex] = useState(0);
  const [filters, setFilters] = useState<PaymentRequestFilter>(
    {} as PaymentRequestFilter
  );
  const [filterValue, setFilterValue] = useState<PaymentRequestFilterValue>(() => {
    const savedFilterValues = sessionStorage.getItem("paymentRequestFilterValues");
    return savedFilterValues
      ? JSON.parse(savedFilterValues)
      : { schoolYearId: "", date: "" };
  });

  const [paymentRequestsPaged, setPaymentRequestsPaged] = useState<
    PagedResult<PaymentRequestList>
  >({} as PagedResult<PaymentRequestList>);

  const [canAddNew, setCanAddNew] = useState<boolean>(false);

  const { page, pageSize, handleChangePage, handleChangepageSize } = useTable();
  const { handleSelectChange } = useInput();

  useEffect(() => {
    getFilters();
  }, [tabIndex]);

  useEffect(() => {
    canAddRequest();
  }, []);

  useEffect(() => {
    if (filterValue.schoolYearId) {
      search();
    }
  }, [filterValue, page, pageSize]);

  useEffect(() => {
    sessionStorage.setItem("paymentRequestFilterValues", JSON.stringify(filterValue));
  }, [filterValue]);

  const clearFilters = async () => {
    const apiClient = new PaymentRequestApiClient();
    const searchFilters = await apiClient.getSearchFilters();
  
    const defaultSchoolYearId =
      searchFilters.schoolYears.length > 0 ? searchFilters.schoolYears[0].id : "";
    const defaultFilterValues = { schoolYearId: defaultSchoolYearId, date: "" };
    setFilterValue(defaultFilterValues);
    sessionStorage.setItem("paymentRequestFilterValues", JSON.stringify(defaultFilterValues));
  };
  



  async function canAddRequest() {
    const apiClient = new PaymentRequestApiClient();
    const canAdd = await apiClient.getCanAddNewRequest();
    setCanAddNew(canAdd);
  }

  async function getFilters() {
    const apiClient = new PaymentRequestApiClient();
    const searchFilters = await apiClient.getSearchFilters();
  
    const defaultSchoolYearId =
      searchFilters.schoolYears.length > 0 ? searchFilters.schoolYears[0].id : "";
  
    setFilterValue((prev) => ({
      ...prev,
      schoolYearId: prev.schoolYearId || defaultSchoolYearId,
    }));
  
    setFilters(searchFilters);
  }
  

  async function search() {
    const apiClient = new PaymentRequestApiClient();
    const requests = await apiClient.search(filterValue, page + 1, pageSize);

    setPaymentRequestsPaged(requests);
  }

  const getYearMonths = () => {
    let selectedYear: YearFilter | undefined;
    if (filterValue.schoolYearId && filters.schoolYears) {
      selectedYear = filters.schoolYears.find(
        (s) => s.id === filterValue.schoolYearId
      );
    }

    return (
      (selectedYear && [
        { startingDate: "", name: "" } as MonthFilter,
        ...selectedYear.months,
      ]) ||
      []
    );
  };

  const tableHeadMetadata: TrTableHeadMetadata<PaymentRequestList>[] = [
    {
      id: "month",
      label: "Mjesec",
      dataName: "monthName",
    },
    { id: "schoolYear", label: "Školska godina", dataName: "schoolYear" },
    {
      id: "amount",
      label: "Iznos",
      dataName: "resolvedCost",
      dataFunc: (p: PaymentRequestList) => currencyFormat(p.resolvedCost || 0, p.currency),
    },
    {
      id: "status",
      label: "Status",
      dataName: "statusName",
      dataFunc: (p: PaymentRequestList) => (
        <PaymentRequestStatusElement
          statusId={p.statusId}
          statusName={p.statusName}
        />
      ),
    },
    {
      id: "viewBtn",
      label: "",
      dataFunc: (p: PaymentRequestList) => (
        <div className={classes.rowRight}>
          {p.statusId == PaymentRequestStatusEnum.Draft ||
            p.statusId == PaymentRequestStatusEnum.Returned ? (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() =>
                history.push(paymentRequestEditUrl(p.id.toString()))
              }
            >
              UREDI
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() =>
                history.push(paymentRequestDetailsUrl(p.id.toString()))
              }
            >
              PREGLED
            </Button>
          )}
        </div>
      ),
    },
  ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: paymentRequestsPaged.data,
  };

  const handleBodyCellCreated = (tableCell: ReactElement, rowData: any) => {
    return (
      <TableCell
        key={tableCell.key}
        {...tableCell.props}
        className={
          rowData.statusId === PaymentRequestStatusEnum.Rejected
            ? classes.redColor
            : ""
        }
      />
    );
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Typography variant="h5">
            Lista mjesečnih zahtjeva za isplatu
          </Typography>
        </Grid>
        <Grid item sm={12}>
          <Box textAlign="right">
            <Button
              variant="contained"
              color="primary"
              component={LinkAdapter}
              to={paymentRequestNewUrl()}
              disabled={!canAddNew}
            >
              <AddIcon />
              Dodaj novi zahtjev
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <TrSelect
            label={"Šk. godina"}
            value={filterValue.schoolYearId}
            name={nameof<typeof filterValue>("schoolYearId")}
            fullWidth={true}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: filters.schoolYears || [],
              } as TrSelectData<YearFilter>
            }
            onChange={(e) => handleSelectChange(e, setFilterValue)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TrSelect
            label={"Mjesec"}
            value={filterValue.date}
            name={nameof<typeof filterValue>("date")}
            fullWidth={true}
            selectData={
              {
                valueProp: "startingDate",
                textProp: "name",
                data: getYearMonths() || [],
              } as TrSelectData<MonthFilter>
            }
            onChange={(e) => handleSelectChange(e, setFilterValue)}
          />
        </Grid>
        <Grid item xs={12} style={{ textAlign: 'right' }}>
          <Link component="button" onClick={clearFilters} variant="body2" style={{ cursor: 'pointer' }}>
            Očisti filtere
          </Link>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item md={12} lg={12}>
          <Paper>
            <TrTable
              hover={true}
              headMetadata={tableHeadMetadata}
              bodyData={tableBodyData}
              enablePagination={true}
              page={page}
              pageSize={pageSize}
              totalCount={paymentRequestsPaged.count}
              noDataMessage={"Trenutno nema zahtjeva za isplatu"}
              onChangePage={handleChangePage}
              onChangePageSize={handleChangepageSize}
              onBodyCellCreated={handleBodyCellCreated}
            />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  redColor: {
    color: "#d9534f",
  },
  rowRight: {
    width: "100%",
    textAlign: "right",
  },
}));
